/** Jagged ground crack extending outward from an impact point.
 *
 * Renders a forked, irregular line along the ground plane — like
 * cracked earth radiating from a stomp or quake. Canvas2D provides
 * antialiased edges in pixelArt mode. Each play() generates a unique
 * crack pattern via seeded pseudo-random offsets.
 */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { CRISP_SCALE } from '../../ui/CrispUtil';

export interface GroundFissureConfig extends VfxPrimitiveConfig {
  /** Maximum length of the main fissure line, in logical pixels. */
  length: number;
  /** Stroke width of the crack lines, in logical pixels. */
  lineWidth: number;
  /** Number of line segments in each crack arm. More = more jagged detail. */
  segments: number;
  /** Maximum lateral deviation per segment, in logical pixels. Larger = more jagged. */
  jitter: number;
  /** Number of crack arms radiating from center (1 = single line, 3+ = radial burst). */
  armCount: number;
  /** Duration of the crack growth animation in ms. */
  growDurationMs: number;
  /** Duration of the fade-out after full extension in ms. */
  fadeDurationMs: number;
  /** Direction of the primary arm, in radians (0 = right). Ignored when armCount > 1. */
  direction: number;
}

const POOL_SIZE = 2;

/** Max number of crack pattern variants to keep in the texture cache. */
const MAX_VARIANTS = 8;

export class GroundFissure extends VfxPrimitive<GroundFissureConfig> {
  private poolIndex = 0;
  private seed = 0;
  /** Track generated texture keys so we can clean up on destroy and cap growth. */
  private generatedKeys!: string[];

  protected allocatePool(): void {
    this.generatedKeys = [];
    // Pre-generate one texture per pool slot — each gets a unique crack pattern.
    for (let i = 0; i < POOL_SIZE; i++) {
      const key = this.generateCrackTexture(i);
      this.generatedKeys.push(key);
      const img = this.createPoolImage(key);
      img.setOrigin(0.5, 0.5);
    }
  }

  play(x: number, y: number, depth: number): void {
    const { config } = this;
    const img = this.pool[this.poolIndex % this.pool.length];
    this.poolIndex++;

    // Regenerate crack pattern for visual variety, capping total variants.
    this.seed++;
    const key = this.generateCrackTexture(this.seed);
    img.setTexture(key);
    this.generatedKeys.push(key);
    this.evictOldTextures();

    this.scene.tweens.killTweensOf(img);
    this.activate(img, x, y, depth);

    // Grow from center: start at scale 0, expand to full.
    img.setScale(0);
    img.setAlpha(1);

    const growTarget = 1 / CRISP_SCALE;

    this.scene.tweens.add({
      targets: img,
      scaleX: growTarget,
      scaleY: growTarget,
      duration: config.growDurationMs,
      ease: 'Quad.easeOut',
      onComplete: () => {
        // Hold at full size briefly, then fade.
        this.scene.tweens.add({
          targets: img,
          alpha: 0,
          duration: config.fadeDurationMs,
          ease: 'Linear',
          onComplete: () => this.deactivate(img),
        });
      },
    });
  }

  stop(): void {
    for (const img of this.pool) {
      if (img.active) {
        this.scene.tweens.killTweensOf(img);
        this.scene.tweens.add({
          targets: img,
          alpha: 0,
          duration: 100,
          onComplete: () => this.deactivate(img),
        });
      }
    }
  }

  destroy(): void {
    for (const key of this.generatedKeys) {
      if (this.scene.textures.exists(key)) {
        this.scene.textures.remove(key);
      }
    }
    this.generatedKeys.length = 0;
    super.destroy();
  }

  /** Evict oldest texture variants beyond MAX_VARIANTS to cap memory. */
  private evictOldTextures(): void {
    // Keep only the most recent MAX_VARIANTS keys; skip keys currently assigned to pool images.
    const activeKeys = new Set(this.pool.map(img => img.texture?.key));
    while (this.generatedKeys.length > MAX_VARIANTS) {
      const oldest = this.generatedKeys[0];
      if (!activeKeys.has(oldest) && this.scene.textures.exists(oldest)) {
        this.scene.textures.remove(oldest);
      }
      this.generatedKeys.shift();
    }
  }

  /** Generate a canvas texture with jagged crack lines radiating from center. */
  private generateCrackTexture(seedVal: number): string {
    const { length, lineWidth, segments, jitter, armCount } = this.config;
    const len = Math.ceil(length * CRISP_SCALE);
    const lw = Math.ceil(lineWidth * CRISP_SCALE);
    const jit = Math.ceil(jitter * CRISP_SCALE);
    const key = `_fissure-${len}-${lw}-${segments}-${armCount}-s${seedVal}`;

    if (this.scene.textures.exists(key)) return key;

    const size = len * 2 + lw * 2 + 4;
    const cx = size / 2;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = lw;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Simple seeded PRNG (mulberry32)
    let s = seedVal | 0;
    const rand = (): number => {
      s = (s + 0x6d2b79f5) | 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };

    const angleStep = (Math.PI * 2) / armCount;
    const baseAngle = armCount === 1 ? 0 : rand() * angleStep;

    for (let arm = 0; arm < armCount; arm++) {
      const angle = baseAngle + arm * angleStep;
      ctx.beginPath();
      ctx.moveTo(cx, cx);

      let px = cx;
      let py = cx;
      const segLen = len / segments;

      for (let seg = 1; seg <= segments; seg++) {
        const lateralOffset = (rand() - 0.5) * 2 * jit;
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);

        px += cosA * segLen + (-sinA) * lateralOffset;
        py += sinA * segLen + cosA * lateralOffset;
        ctx.lineTo(px, py);
      }
      ctx.stroke();
    }

    this.scene.textures.addCanvas(key, canvas);
    this.scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
    return key;
  }
}
